export type SubscriberStatus = "pending" | "confirmed" | "unsubscribed";

export type UnlockCheck = { unlocked: boolean; status?: SubscriberStatus } | undefined;

export type UnlockPhase = "closed" | "checking" | "form" | "pending" | "unlocked";

export type SubscribeOutcome = {
  email: string;
  status: SubscriberStatus;
  alreadySubscribed: boolean;
  confirmationSent: boolean;
};

export const UNLOCK_REQUIRES_CONFIRMATION_MESSAGE = "Confirm your email first. Check your inbox for the link.";

export function isUnlockedStatus(check: UnlockCheck): boolean {
  return Boolean(check?.unlocked && check.status === "confirmed");
}

export function resolveUnlockPhase(input: {
  storedEmail: string | null;
  check: UnlockCheck;
  hasTarget: boolean;
}): UnlockPhase {
  const { storedEmail, check, hasTarget } = input;
  if (storedEmail && isUnlockedStatus(check)) return "unlocked";
  if (storedEmail && check === undefined) return hasTarget ? "checking" : "closed";
  if (!hasTarget) return "closed";
  if (storedEmail && check?.status === "pending") return "pending";
  return "form";
}

export function describeSubscribeOutcome(outcome: SubscribeOutcome): { phase: "unlocked" | "pending"; message: string } {
  if (outcome.status === "confirmed") {
    return { phase: "unlocked", message: `${outcome.email} is already confirmed. Your download is starting.` };
  }
  if (!outcome.confirmationSent) {
    return {
      phase: "pending",
      message: `A confirmation link is already on its way to ${outcome.email}. Check your inbox and spam folder.`,
    };
  }
  return {
    phase: "pending",
    message: outcome.alreadySubscribed
      ? `Another confirmation link is on its way to ${outcome.email}. Click it and this page unlocks.`
      : `Confirmation link sent to ${outcome.email}. Click it and this page unlocks.`,
  };
}

export const ARTIST_GATE_OPENS_EVERY_ARTIST = true;

export function normalizeArtistSlug(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const value = String(raw).trim().toLowerCase().slice(0, 120);
  return value.length > 0 ? value : null;
}

export function resolveSignupArtist(existing: string | null | undefined, incoming: string | null | undefined): string | null {
  const kept = normalizeArtistSlug(existing);
  if (kept) return kept;
  return normalizeArtistSlug(incoming);
}

export const ARTIST_GATE_REQUIRES_CONFIRMATION = true;

export function resolveArtistGatePhase(input: { storedEmail: string | null; check: UnlockCheck }): UnlockPhase {
  if (!ARTIST_GATE_REQUIRES_CONFIRMATION) {
    return input.storedEmail ? "unlocked" : "form";
  }
  return resolveUnlockPhase({ storedEmail: input.storedEmail, check: input.check, hasTarget: true });
}

export const UNLOCK_LINK_SENT_MESSAGE = "You are already confirmed. A link to open this on this device is on its way to";

export function needsUnlockLink(input: { status: SubscriberStatus; hasUnlockToken: boolean }): boolean {
  return input.status === "confirmed" && !input.hasUnlockToken;
}

export function describeArtistGateOutcome(outcome: SubscribeOutcome): { phase: "unlocked" | "pending"; message: string } {
  if (outcome.status === "confirmed") {
    return { phase: "unlocked", message: `${outcome.email} is already confirmed. Opening the page.` };
  }
  if (!outcome.confirmationSent) {
    return {
      phase: "pending",
      message: `A confirmation link is already on its way to ${outcome.email}. Look in your inbox and your spam folder.`,
    };
  }
  return {
    phase: "pending",
    message: outcome.alreadySubscribed
      ? `Another confirmation link is on its way to ${outcome.email}. Click it and this page opens.`
      : `Confirmation link sent to ${outcome.email}. Click it and this page opens.`,
  };
}

const UNSAFE_NEXT_CHARS = new Set([String.fromCharCode(60), String.fromCharCode(62), String.fromCharCode(34), String.fromCharCode(39), String.fromCharCode(96), String.fromCharCode(92)]);

const hasUnsafeChar = (value: string) => {
  for (const char of value) {
    if (char.charCodeAt(0) <= 0x20 || UNSAFE_NEXT_CHARS.has(char)) return true;
  }
  return false;
};

export function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let value = String(raw).trim();
  if (!value) return null;
  try {
    value = decodeURIComponent(value);
  } catch {
    return null;
  }
  if (!value.startsWith("/")) return null;
  if (value.startsWith("//")) return null;
  if (hasUnsafeChar(value)) return null;
  if (/^\/+(admin|newsletter)(\/|$)/i.test(value)) return null;
  if (value.length > 400) return null;
  return value;
}

export function confirmUrlWithNext(baseConfirmUrl: string, pagePath: string | null | undefined): string {
  const next = safeNextPath(pagePath);
  if (!next) return baseConfirmUrl;
  const separator = baseConfirmUrl.includes("?") ? "&" : "?";
  return `${baseConfirmUrl}${separator}next=${encodeURIComponent(next)}`;
}

export function releaseTitleFromPath(path: string | null | undefined): string | null {
  const safe = safeNextPath(path);
  if (!safe) return null;
  const slug = safe.split("?")[0].split("#")[0].split("/").filter(Boolean).pop();
  if (!slug) return null;
  const words = slug.split("-").filter(Boolean);
  if (words.length === 0) return null;
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}
